import React, { useCallback, useEffect, useState } from "react";

import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import { Button, DrawerProps, IconButton, Drawer as MuiDrawer, Stack, styled, Typography } from "@mui/material";
import { PdfExtractorIcon } from "assets/material-custom-icons";
import { useNavigate } from "hooks";

import { TagTable, TagTableProps } from "./TagTable";

const defaultDrawerHeight = 400;

const MuiPermanentDrawer = (props: DrawerProps & { resizedWidth: number }) => <MuiDrawer {...props} />;

const Drawer = styled(MuiPermanentDrawer, { shouldForwardProp: (prop) => prop !== "open" && prop !== "maximized" })(
  ({ resizedWidth }) => ({
    width: defaultDrawerHeight,
    height: resizedWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box"
  })
);

interface Props extends TagTableProps {
  isAddTagAvailable: boolean;
  dealId: number | null; // dealId is given if all documents belong to the same deal, otherwise null.
  onAddTag: () => void;
  onDownload: () => void;
  onSave: () => void;
  open: boolean;
  setOpen: (value: boolean) => void;
  isSaveDisabled: boolean;
}

export const TableDrawer: React.FC<Props> = (props) => {
  const { onAddTag, onDownload, onSave, dealId, open = false, setOpen, isSaveDisabled, ...tableProps } = props;
  const [drawerHeight, setDrawerHeight] = useState<number>(defaultDrawerHeight);
  const minDrawerHeight = 50;
  const maxDrawerHeight = 1000;
  const toggleOpen = useCallback(() => {
    setOpen(!open);
    setDrawerHeight(defaultDrawerHeight);
  }, [open]);
  const navigate = useNavigate();

  const handleMouseDown = () => {
    document.addEventListener("mouseup", handleMouseUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
    setOpen(true);
  };

  const handleMouseUp = () => {
    document.removeEventListener("mouseup", handleMouseUp, true);
    document.removeEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const newWidth = document.body.offsetTop + document.body.offsetHeight - e.clientY + 20;
    if (newWidth > minDrawerHeight && newWidth < maxDrawerHeight) {
      setDrawerHeight(newWidth);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setDrawerHeight(minDrawerHeight);
    }
    if (open) {
      setDrawerHeight(defaultDrawerHeight);
    }
  }, [open]);

  return (
    <Drawer
      sx={{
        ".MuiPaper-root": {
          display: "flex",
          direction: "column",
          justifyContent: "flex-start",
          alignItems: "flex-end",
          position: "absolute",
          zIndex: "1000", // should be less than 1200 - sidebar's zIndex
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "12px 18px 18px 18px",
          borderTop: "1px solid #0a3142",
          gap: "16px",
          ...(open && { paddingTop: "7px" })
        }
      }}
      variant="permanent"
      anchor="bottom"
      onClose={() => setOpen(false)}
      open={open}
      resizedWidth={drawerHeight}
      PaperProps={{ style: { height: drawerHeight, overflow: "hidden" } }}
    >
      <Stack
        direction="row"
        sx={{
          position: "absolute",
          width: "100%",
          height: "3px",
          top: "0",
          right: "-1px",
          bottom: "0",
          cursor: "row-resize"
        }}
        onMouseDown={handleMouseDown}
      />
      <Stack
        direction="row"
        justifyContent={open ? "space-between" : "flex-end"}
        alignItems="center"
        sx={{ gap: "20px", height: "35px", zIndex: "1", backgroundColor: "#ffffff", width: "100%" }}
      >
        <Typography sx={{ marginRight: "15px" }}>Tagged values</Typography>
        <Stack direction="row" gap="20px">
          {open && (
            <>
              <IconButton disabled={!props.isAddTagAvailable} onClick={onAddTag}>
                <AddCircleOutline />
              </IconButton>
              <IconButton
                disabled={!dealId}
                onClick={() => {
                  if (!dealId) return;
                  navigate({ route: "dealMonitoringData", params: { deal_id: dealId } });
                }}
              >
                <InsertChartOutlinedIcon />
              </IconButton>

              <IconButton disabled={!dealId} onClick={onDownload}>
                <PdfExtractorIcon sx={{ fill: !dealId ? "rgba(0, 0, 0, 0.26)" : "#071F35" }} />
              </IconButton>
            </>
          )}

          <IconButton
            onClick={toggleOpen}
            sx={{
              cursor: "pointer",
              borderRadius: "18px",
              height: "20px",
              width: "20px",
              margin: "5px",
              color: "white",
              transition: "transform 300ms",
              backgroundColor: "#0B4549",
              ...(!open && { transform: "rotate(180deg)" }),
              ":hover": {
                backgroundColor: "#0B4549"
              }
            }}
          >
            <ExpandMore sx={{ fontSize: "18px" }} />
          </IconButton>
        </Stack>
      </Stack>
      <TagTable {...tableProps} />
      <Button disabled={isSaveDisabled} variant="outlined" onClick={onSave}>
        Save
      </Button>
    </Drawer>
  );
};
